import React, { useEffect, useState, useMemo } from 'react'
import server from '../../../api/server.js'
import { FaTimes } from 'react-icons/fa'
import ModernTimeDropdown from '../../layouts/modernDropdown.jsx'
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker.jsx"
import { useNavigate } from 'react-router-dom'
import { NumberCard } from '../../layouts/ChartLayout.jsx'
import "../../../dashboard-components/loader.css"
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'

const AgentFeedbackTraining = ({serviceID, clientID}) => {
    const [service_id, setService_id] = useState(null)
    const [client_id, setClient_id] = useState(null)
    const [email, setEmail] = useState(null)
    const [reportsData, setReportsData] = useState([])
    const [selectedTrainingId, setSelectedTrainingId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [showErrorCases, setShowErrorCases] = useState(false)
    const [errorReports, setErrorReports] = useState([])
    const [filters, setFilters] = useState({
        employee: null,
        time: 'Last 7 Days',
        completion_status: null,
        customRangeType: 'between',
    })
    const [dummyFilterData, setDummyFilterData] = useState({
        employees: [],
        times: [
          "This Month",
          "Last Month",
          "Last 30 Days",
          "Last 3 Months",
          "Custom Range"],
        progress: [
          'Completed',
          'Ongoing',
          'Pending',
          'Processing',
          'Failed'
        ]
    })
    const [sortConfig, setSortConfig] = useState({ field: null, direction: null })
    const [errorSortConfig, setErrorSortConfig] = useState({ field: null, direction: null })
    const navigate = useNavigate()
    const handleFilterChange = (e, key) => {
        let value = e.target.value;
        if (key === 'time' && value !== 'Custom Range') {
          setFilters((prev) => ({ 
            ...prev, 
            [key]: value,
            customRangeType: 'between',
            customStart: '',
            customEnd: '',
          }));
        } else {
          setFilters((prev) => ({ ...prev, [key]: value }));
        }
    };
    const [role] = useState(()=> {
        const userCookie = getCookie('dashboard_login_user_creds');
        if (userCookie) {
          const userData = JSON.parse(userCookie);
          const role = userData.role
          return role
        }
        return 'admin';
    })

    function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }

    useEffect(() => {
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user) {
          setEmail(user.email);
        }
        if (serviceID) {
          setService_id(serviceID);
        }
        if(clientID){
          setClient_id(clientID)
        }
        if(role === 'employee'){
          handleFilterChange({'target': {'value': user.name}}, 'employee')
        }
    }, [serviceID, clientID, role]);
    
    useEffect(() => {
        if (client_id && service_id && email) {
          getAgentFeedbackTraining()
        }
    }, [client_id, service_id, email, filters]);

    const getAgentFeedbackTraining = async () => {
        try {
            setLoading(true);
            let res = await server.getTrainingDataList(client_id, service_id, email, 'agent-feedback', 'training', filters.time, filters.customStart, filters.customEnd, filters.employee, filters.completion_status ? filters.completion_status.toLowerCase() : null)
            if(res.status === 200){
                let resp = await res.json()
                setReportsData(resp.data)
                
                // Filter error cases from the same data
                const failedReports = resp.data.filter(report => report.status === 'failed')
                setErrorReports(failedReports)
                
                let res1 = await server.getAdvancedFilterDetailsAgentFeedback(client_id, service_id, email, 'agent-feedback', 'training')
                if(res1.status === 200){
                    let resp1 = await res1.json()
                    setDummyFilterData(prev => ({
                        ...prev,
                        employees: resp1.employee_id,
                    }));
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }
    const getReadableDateFromEpoch = (time) => {
        const date = new Date(time * 1000);
        return date.toLocaleDateString('en-GB', {
          day:   '2-digit',
          month: 'short',   // e.g. "Apr"
          year:  'numeric'
        });
      }; 
    const handleViewTrainingDetails = (id) => {
        setSelectedTrainingId(id)
        navigate(`/analytics/${client_id}/${service_id}/agent-feedback/training/${id}`)
    }

    const handleGenerateReport = () => {
        navigate(`/${client_id}/${service_id}/agent-feedback/reports`)
    }

    const create_date_range = (start_time, end_time) => {
        let start_date = new Date(start_time * 1000);
        let end_date = new Date(end_time * 1000);
        return `${start_date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - ${end_date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    }

    const getValueForSort = (item, field) => {
      if (!item) return null;
      switch (field) {
        case 'training_session_id':
          return item.training_session_id != null ? String(item.training_session_id).toLowerCase() : null;
        case 'employee_id':
          return item.employee_id != null ? String(item.employee_id).toLowerCase() : null;
        case 'report_start_time':
          return item.report_start_time != null ? Number(item.report_start_time) : null;
        case 'createdat':
          return item.createdat != null ? Number(item.createdat) : null;
        case 'status':
          return item.status != null ? String(item.status).toLowerCase() : null;
        case 'completion_status':
          return item.completion_status != null ? String(item.completion_status).toLowerCase() : null;
        case 'score_percentage': {
          if (item.score_percentage == null) return null;
          const n = parseInt(item.score_percentage, 10);
          return Number.isNaN(n) ? null : n;
        }
        case 'due_at':
          return item.due_at != null ? Number(item.due_at) : null;
        case 'error':
          return item.error != null ? String(item.error).toLowerCase() : null;
        default: {
          const v = item[field];
          if (v == null) return null;
          const num = Number(v);
          if (!Number.isNaN(num) && String(v).trim() !== '') return num;
          return String(v).toLowerCase();
        }
      }
    }

    const sortArray = (list, field, direction) => {
      if (!field || !direction) return list;
      const arr = [...list];
      const factor = direction === 'asc' ? 1 : -1;
      arr.sort((a, b) => {
        const va = getValueForSort(a, field);
        const vb = getValueForSort(b, field);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return factor * (va - vb);
        return factor * String(va).localeCompare(String(vb));
      });
      return arr;
    }

    const arrowClass = (cfg, field, dir) => (cfg.field === field && cfg.direction === dir ? 'text-indigo-600' : 'text-gray-300 group-hover:text-gray-400');
    const getAriaSort = (cfg, field) => (cfg.field === field ? (cfg.direction === 'asc' ? 'ascending' : 'descending') : 'none');
    const cycleSort = (cfg, setCfg, field) => {
      let nextDir = 'asc';
      if (cfg.field === field) {
        nextDir = cfg.direction === 'asc' ? 'desc' : (cfg.direction === 'desc' ? null : 'asc');
      }
      setCfg(nextDir ? { field, direction: nextDir } : { field: null, direction: null });
    };
    const renderArrows = (cfg, field, setCfg) => {
      if (cfg.field === field && cfg.direction === 'asc') {
        return (
          <ArrowUp className={`h-3 w-3 ${arrowClass(cfg, field, 'asc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
        );
      }
      if (cfg.field === field && cfg.direction === 'desc') {
        return (
          <ArrowDown className={`h-3 w-3 -mt-0.5 ${arrowClass(cfg, field, 'desc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'desc' });}} />
        );
      }
      return (
        <ChevronsUpDown className={`h-3 w-3 ${arrowClass(cfg, field, 'none')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      );
    };

    const displayReports = useMemo(() => (
      sortConfig.field ? sortArray(reportsData.filter(r => r.status !== 'failed'), sortConfig.field, sortConfig.direction) : reportsData.filter(r => r.status !== 'failed')
    ), [reportsData, sortConfig]);

    const displayErrorReports = useMemo(() => (
      errorSortConfig.field ? sortArray(errorReports, errorSortConfig.field, errorSortConfig.direction) : errorReports
    ), [errorReports, errorSortConfig]);

    // Helper function to get status styling
    const getStatusStyle = (status) => {
      const normalizedStatus = status?.toLowerCase();
      switch (normalizedStatus) {
        case 'received':
          return {
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            text: 'Queued'
          };
        case 'processing':
          return {
            backgroundColor: '#e0e7ff',
            color: '#4338ca',
            text: 'Processing'
          };
        case 'success':
          return {
            backgroundColor: '#dcfce7',
            color: '#166534',
            text: 'Success'
          };
        case 'failed':
          return {
            backgroundColor: '#fecaca',
            color: '#dc2626',
            text: 'Failed'
          };
        default:
          return {
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            text: status || 'Unknown'
          };
      }
    };

    // Calculate metrics from table data (only include status = 'success')
    const calculateMetrics = () => {
        const successData = reportsData.filter(report => report.status === 'success');
        
        const totalSessions = successData.length;
        const completedSessions = successData.filter(report => report.completion_status === 'completed').length;
        const ongoingSessions = successData.filter(report => 
            report.completion_status === 'in_progress' || report.completion_status === 'ongoing'
        ).length;
        const pendingSessions = successData.filter(report => report.completion_status === 'pending').length;
        
        // Calculate average score from sessions with score_percentage, excluding pending
        const sessionsWithScore = successData.filter(report => 
            report.score_percentage !== null && 
            report.score_percentage !== undefined && 
            report.completion_status !== 'pending'
        );
        const averageScore = sessionsWithScore.length > 0 
            ? Math.round(sessionsWithScore.reduce((sum, report) => sum + parseInt(report.score_percentage || 0), 0) / sessionsWithScore.length)
            : 0;

        return [
            {
                id: 'totalSessions',
                title: 'Total Sessions',
                value: totalSessions,
                change: undefined,
                color: '#4F46E5',
                allowVisit: false,
                hideBorder: true,
                formatter: (value) => value
            },
            {
                id: 'completedSessions',
                title: 'Completed Sessions',
                value: completedSessions,
                change: undefined,
                color: '#10B981',
                allowVisit: false,
                hideBorder: true,
                formatter: (value) => value
            },
            {
                id: 'ongoingSessions',
                title: 'Ongoing Sessions',
                value: ongoingSessions,
                change: undefined,
                color: '#3B82F6',
                allowVisit: false,
                hideBorder: true,
                formatter: (value) => value
            },
            {
                id: 'pendingSessions',
                title: 'Pending Sessions',
                value: pendingSessions,
                change: undefined,
                color: '#F59E0B',
                allowVisit: false,
                hideBorder: true,
                formatter: (value) => value
            },
            {
                id: 'averageScore',
                title: 'Average Score',
                value: averageScore,
                change: undefined,
                color: '#8B5CF6',
                allowVisit: false,
                hideBorder: true,
                formatter: (value) => (
                    <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded h-2">
                            <div className={`h-2 rounded bg-green-500`} style={{ width: `${value}%` }}></div>
                        </div>
                        <span className="text-sm">{value}%</span>
                    </div>
                )
            }
        ];
    };

    const sessionMetrics = calculateMetrics();
  return (
    <div className="py-3 px-5 bg-gray-50 font-sans">
        <h1 class="text-xl font-semibold mb-2">
              <span class="text-gray-500">Agent Feedback &gt;</span>
              <span class="text-gray-900">  Training</span>
        </h1>
        <div className="text-sm text-gray-500">View and manage training sessions for your team</div>
            <div className="bg-white rounded-lg shadow-sm p-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                    <div className="text-sm font-medium text-gray-700">Quick Filters</div>
                </div>
                <div className="flex gap-4 mb-4">
                        <ModernTimeDropdown isDisabled={role === 'employee'} placeholder="Select Employee" value={filters.employee} onChange={(e) => handleFilterChange(e, 'employee')} options={dummyFilterData.employees}/>
                        <ModernTimeDropdown placeholder="Select Time Duration" value={filters.time} onChange={(e) => handleFilterChange(e, 'time')} options={dummyFilterData.times}/>
                        <ModernTimeDropdown placeholder="Select Progress" value={filters.completion_status} onChange={(e) => handleFilterChange(e, 'completion_status')} options={dummyFilterData.progress}/>
                </div>
                        {filters.time === "Custom Range" && (
                          <div className="mb-2">
                            <ModernDateRangePicker
                              startDate={filters.customStart}
                              endDate={filters.customEnd}
                              rangeType={filters.customRangeType || "between"}
                              maxRangeDays={90}
                              placeholder="Select Time Range"
                              onChange={({ startDate, endDate, rangeType }) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  customStart: startDate,
                                  customEnd: endDate,
                                  customRangeType: rangeType || "between",
                                }))
                              }
                            />
                          </div>
                        )}
                <div className="flex gap-4 flex-wrap">
                        {Object.keys(filters).map(
                          (key) =>{
                            if (key === 'customStart' || key === 'customEnd' || key === 'customRangeType') {
                              return null;
                            }
                            if (key === 'time' && filters.time === 'Custom Range') {
                              return null;
                            }
                            const displayNameMap = { completion_status: 'Progress' };
                            return filters[key] && (
                              <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                                <span className="capitalize text-indigo-600">{displayNameMap[key] || key} - {filters[key]}</span>
                                {key!=='time' && !(role === 'employee' && key === 'employee') && <button
                                  className="ml-2 text-gray-600"
                                  onClick={() => setFilters((prev) => ({ ...prev, [key]: null }))}
                                >
                                  <FaTimes />
                                </button>}
                              </div>
                            )
                          })}
                          {filters.time === "Custom Range" && filters.customStart && (filters.customRangeType === "after" || filters.customEnd) && (
                            <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                              <span className="text-indigo-600">
                                time - {filters.customStart} {filters.customRangeType === "after" ? "onwards" : `to ${filters.customEnd}`}
                              </span>
                              <button
                                className="ml-2 text-gray-600"
                                onClick={() => setFilters((prev) => ({ ...prev, time: '', customRangeType: 'between', customStart: '', customEnd: '' }))}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          )}
                  </div>
                <div className='flex items-center justify-end w-full'>
                    <input type="checkbox" onChange={(e)=>setShowErrorCases(e.target.checked)}/>
                    <label className='px-2 py-1.5 text-sm font-medium text-gray-700 focus:outline-none'>Show Error Cases</label>
                </div>
            </div>
            {/* Session Metrics Cards */}
            {!loading && !showErrorCases && (
                <div style={{display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: "1rem", padding: "1rem 0", height: "150px"}}>
                    {sessionMetrics.map((metric, idx) => (
                        <div key={idx} style={{background: "white", borderRadius: "10px"}} className="rounded-xl shadow-sm border">
                            <NumberCard key={idx} metric={metric}/>
                        </div>
                    ))}
                </div>
            )}
            <div>
                <div>
                    <div style={{width: "100%", background: "white"}} className="rounded-lg shadow-sm">
                      {!loading && !showErrorCases && <div>
                        <div className="rounded-lg overflow-hidden">
                          <div className="overflow-x-auto" style={{maxWidth: "calc(100vw - 18rem)"}}>
                            <div style={{maxHeight: "50vh", overflow: "auto"}}>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead style={{ position: "sticky", top: "0", background: "white"}}>
                                    <tr className="text-left">
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'training_session_id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider rounded-tl-lg" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'training_session_id')}>
                                            <span>Training Session ID</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'training_session_id', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'employee_id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'employee_id')}>
                                            <span>Employee ID</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'employee_id', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'report_start_time')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'report_start_time')}>
                                            <span>Date Range</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'report_start_time', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'createdat')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'createdat')}>
                                            <span>Created At</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'createdat', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'status')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'status')}>
                                            <span>Status</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'status', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'completion_status')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'completion_status')}>
                                            <span>Progress</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'completion_status', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'score_percentage')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'score_percentage')}>
                                            <span>Score</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'score_percentage', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" aria-sort={getAriaSort(sortConfig, 'due_at')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                          <button type="button" className="flex items-center group" onClick={()=>cycleSort(sortConfig, setSortConfig, 'due_at')}>
                                            <span>Due At</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(sortConfig, 'due_at', setSortConfig)}</span>
                                          </button>
                                        </th>
                                        <th scope="col" className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider rounded-tr-lg" style={{ fontSize: "13px", fontWeight: "400"}}>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {displayReports.map((report) => (
                                        <tr key={report.id} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{report.training_session_id}</td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{report.employee_id}</td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{create_date_range(report.report_start_time, report.report_end_time)}</td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{new Date(report?.createdat*1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                            <span style={{
                                                fontSize: "0.7rem",
                                                textTransform: "capitalize",
                                                backgroundColor: getStatusStyle(report.status).backgroundColor,
                                                color: getStatusStyle(report.status).color,
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                display: 'inline-block'
                                            }}>
                                                {getStatusStyle(report.status).text}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                            <span style={{
                                                fontSize: "0.7rem",
                                                textTransform: "capitalize",
                                                backgroundColor: report.completion_status === 'pending' ? '#fef3cd' : 
                                                              report.completion_status === 'in_progress' || report.completion_status === 'ongoing' ? '#e0e7ff' : 
                                                              report.completion_status === 'completed' ? '#dcfce7' : '#f3f4f6',
                                                color: report.completion_status === 'pending' ? '#92400e' : 
                                                       report.completion_status === 'in_progress' || report.completion_status === 'ongoing' ? '#4338ca' : 
                                                       report.completion_status === 'completed' ? '#166534' : '#374151',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                display: 'inline-block'
                                            }}>
                                                {report.completion_status?.replace('_', ' ') || 'Not Set'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                                {report.score_percentage !== null && report.score_percentage !== undefined ? (
                                                    <>
                                                        <div className="w-12 bg-gray-200 rounded-full h-2">
                                                            <div className={`h-2 rounded-full bg-green-500`} style={{ width: `${parseInt(report.score_percentage)}%` }}></div>
                                                        </div>
                                                        <span className="text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{parseInt(report.score_percentage)}%</span>
                                                    </>
                                                ) : (
                                                    <span className="text-sm text-gray-400" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>N/A</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{new Date(report?.due_at*1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem", cursor: "pointer"}} onClick={()=>handleViewTrainingDetails(report.training_session_id)}>View Details</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                          </div>
                        </div>
                      </div>}
                      {!loading && showErrorCases && (
                        <div className="rounded-lg overflow-hidden">
                          <div className="overflow-x-auto" style={{maxWidth: "calc(100vw - 18rem)"}}>
                            <div style={{maxHeight: "50vh", overflow: "auto"}}>
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead style={{ position: "sticky", top: "0", background: "white"}}>
                                  <tr className="text-left">
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'training_session_id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'training_session_id')}>
                                        <span>Training Session ID</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'training_session_id', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'employee_id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'employee_id')}>
                                        <span>Employee ID</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'employee_id', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'report_start_time')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'report_start_time')}>
                                        <span>Date Range</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'report_start_time', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'createdat')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'createdat')}>
                                        <span>Created At</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'createdat', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'status')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'status')}>
                                        <span>Status</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'status', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                    <th scope="col" aria-sort={getAriaSort(errorSortConfig, 'error')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{ fontSize: "13px", fontWeight: "400"}}>
                                      <button type="button" className="flex items-center group" onClick={()=>cycleSort(errorSortConfig, setErrorSortConfig, 'error')}>
                                        <span>Error</span>
                                        <span className="ml-1 flex flex-col leading-none">{renderArrows(errorSortConfig, 'error', setErrorSortConfig)}</span>
                                      </button>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {displayErrorReports.map((report) => (
                                    <tr key={report.id} className="border-b hover:bg-gray-50">
                                      <td className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{report.training_session_id}</td>
                                      <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{report.employee_id}</td>
                                      <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{create_date_range(report.report_start_time, report.report_end_time)}</td>
                                      <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{new Date(report?.createdat*1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                      <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                        <span style={{
                                          fontSize: "0.7rem",
                                          textTransform: "capitalize",
                                          backgroundColor: getStatusStyle(report.status).backgroundColor,
                                          color: getStatusStyle(report.status).color,
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          display: 'inline-block'
                                        }}>
                                          {getStatusStyle(report.status).text}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{report.error || 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                      {loading && (
                        <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                          <div className="loader"></div>
                          <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                        </div>
                      )}
                    </div>
                </div>
            </div>
        </div>
  )
}

export default AgentFeedbackTraining;